import { useCart } from "./CartContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import usePlatformFees from "./hooks/usePlatformFees";
import { 
  ArrowLeft, 
  CheckCircle2, 
  Phone, 
  MapPin, 
  Wallet, 
  FileText, 
  Camera, 
  ShoppingBag,
  Info,
  ChevronDown,
  Clock,
  Copy,
  Check,
  Zap
} from "lucide-react";

// Sous-composant pour la gestion de la copie des numéros de paiement
function PaymentBadge({ nomPaiement, numero }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!numero) return;
    navigator.clipboard.writeText(numero);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      type="button"
      className={`px-4 py-3 text-sm font-bold rounded-2xl flex items-center justify-between transition-all border w-full ${
        copied 
          ? "bg-emerald-50 text-emerald-600 border-emerald-200" 
          : "bg-gray-50 text-slate-700 border-gray-100 hover:border-red-200 active:scale-[0.98]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="opacity-60">{nomPaiement}:</span>
        <span className={copied ? "text-emerald-600" : "text-[#f34141] font-extrabold"}>{numero}</span>
      </div>
      {copied ? <Check size={16} /> : <Copy size={16} className="text-gray-400" />}
    </button>
  );
}

export default function PreCommande() {
  const { cart, total, clearCart } = useCart();
  const { serviceFee, deliveryFee } = usePlatformFees();
  const navigate = useNavigate();

  const phoneFromStorage = localStorage.getItem("customerPhone") || "";

  const [customerPhone, setCustomerPhone] = useState(phoneFromStorage);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [manualAddress, setManualAddress] = useState("");
  const [payment, setPayment] = useState("cash");
  const [deliveryMode, setDeliveryMode] = useState("click_collect");
  const [note, setNote] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [merchantInfos, setMerchantInfos] = useState(null);

  const finalTotal = total + serviceFee + (deliveryMode === "delivery" ? deliveryFee : 0);

  // Récupération des adresses client
  useEffect(() => {
    if (!customerPhone) return;
    api.get(`/users/client/${customerPhone}`)
      .then((res) => {
        const list = res.data.user?.addresses || [];
        setAddresses(list);
        if (list.length > 0) {
          const def = list.find((a) => a.isDefault);
          setSelectedAddress(def || list[0]);
        }
      })
      .catch(() => {
        setAddresses([]);
        setSelectedAddress(null);
      });
  }, [customerPhone]);

  // Récupération des infos de paiement du marchand
  useEffect(() => {
    const merchantId = cart?.[0]?.merchantId || cart?.[0]?.merchant?._id;
    if (merchantId) {
      api.get(`/merchants/public/${merchantId}`)
        .then(res => setMerchantInfos(res.data.merchant))
        .catch(err => console.error("Erreur recup marchand:", err));
    }
  }, [cart]);

  // Force le mode de paiement sur "mobile" si "livraison" est sélectionné
  useEffect(() => {
    if (deliveryMode === "delivery") {
      setPayment("mobile");
    }
  }, [deliveryMode]);

  const confirm = async () => {
    if (!cart || cart.length === 0) return alert("Votre panier est vide.");
    if (!customerPhone || customerPhone.trim().length < 8)
      return alert("Numéro invalide.");

    if (deliveryMode === "delivery" && !paymentProof) {
        return alert("Pour une livraison, la capture d'écran du paiement (Bankily/Masrivi...) est obligatoire.");
    }

    const addressToSend = selectedAddress
      ? `${selectedAddress.label || ""} ${selectedAddress.street || ""}`.trim()
      : manualAddress;

    if (!addressToSend || addressToSend.length < 3)
      return alert("Adresse invalide.");

    const merchantIdToSend = String(
      cart?.[0]?.merchantId || cart?.[0]?.merchant?._id || ""
    ).trim();

    if (!merchantIdToSend) {
      return alert("Erreur : Supermarché introuvable. Veuillez réessayer.");
    }

    try {
      const res = await api.post("/orders", {
        merchantId: merchantIdToSend,
        customerName: "Client Souqi",
        customerPhone,
        address: addressToSend,
        items: cart.map((item) => ({
          productId: item.productId || item._id || item.id || null,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
        })),
        note,
        paymentMethod: payment,
        fulfillmentType: deliveryMode,
      });

      const order = res.data.order;

      if (paymentProof) {
        const fd = new FormData();
        fd.append("image", paymentProof);
        fd.append("phone", customerPhone);
        try {
          setUploadingProof(true);
          await api.post(`/orders/${order._id}/payment-proof`, fd);
        } catch (err) {
          alert("Erreur lors de l’envoi de la preuve.");
        } finally {
          setUploadingProof(false);
        }
      }

      localStorage.setItem("customerPhone", customerPhone);
      clearCart();
      navigate("/commande-envoyee");
    } catch (err) {
      alert(err?.response?.data?.error || "Erreur lors de l’envoi");
    }
  };

  return (
    <div className="min-h-screen px-4 pb-24 font-sans pt-28 bg-[#FAFAFA]">
      <div className="max-w-2xl mx-auto">
        
        {/* BOUTON RETOUR */}
        <div className="flex mb-8 animate-fade-up">
            <button
                onClick={() => navigate("/panier")}
                className="group flex items-center gap-3 px-5 py-2.5 bg-white border border-gray-100 rounded-full shadow-sm text-slate-500 font-bold text-sm transition-all duration-300 hover:shadow-md hover:text-[#f34141] hover:border-red-50"
            >
                <div className="flex items-center justify-center w-6 h-6 transition-colors rounded-full bg-gray-50 group-hover:bg-red-50">
                    <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
                </div>
                <span>Retour au panier</span>
            </button>
        </div>

        <h1 className="mb-10 text-4xl font-black tracking-tight text-slate-900 leading-[1.1]">
          Finaliser ma <span className="text-[#f34141]">commande</span>
        </h1>

        <div className="grid gap-8">
          
          {/* SECTION 1: RÉCAPITULATIF */}
          <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-[2.5rem] animate-fade-up" style={{ animationDelay: '0.05s' }}>
            <div className="p-6 border-b bg-gradient-to-r from-slate-50 to-gray-100 border-gray-200/50">
                <div className="flex items-center gap-3 text-slate-700">
                    <ShoppingBag size={22} className="text-[#f34141]" />
                    <h2 className="text-lg font-bold">Ma sélection</h2>
                </div>
            </div>
            <div className="p-8">
                <div className="pb-6 space-y-3 border-b border-gray-200 border-dashed">
                    <div className="flex justify-between font-medium text-slate-500">
                        <span>Articles ({cart.length})</span>
                        <span className="font-bold text-slate-900">{total} MRU</span>
                    </div>
                    <div className="flex justify-between font-medium text-slate-500">
                        <span>Frais de service Souqi</span>
                        <span className="font-bold text-slate-900">{serviceFee} MRU</span>
                    </div>
                    {deliveryMode === "delivery" && (
                      <div className="flex justify-between font-medium text-slate-500 animate-fade-in">
                        <span>Frais de livraison</span>
                        <span className="font-bold text-slate-900">{deliveryFee} MRU</span>
                      </div>
                    )}
                </div>
                <div className="flex items-center justify-between mt-6">
                    <span className="text-lg font-bold text-slate-400">Total à payer</span>
                    <span className="text-4xl font-black text-[#f34141]">
                        {finalTotal} MRU
                    </span>
                </div>
            </div>
          </div>

          <div className="space-y-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            
            {/* RÉCEPTION & CONTACT */}
            <div className="p-8 bg-white border border-gray-100 shadow-sm rounded-[2.5rem]">
              <div className="grid gap-8 md:grid-cols-2">
                <div>
                  <label className="flex items-center gap-2 mb-4 text-xs font-black tracking-widest text-gray-400 uppercase">
                    <CheckCircle2 size={14} className="text-[#f34141]" />
                    Mode de réception
                  </label>
                  <div className="relative">
                    <select
                        className="w-full p-4 pr-10 font-bold transition-all border-2 border-transparent outline-none appearance-none cursor-pointer bg-gray-50 rounded-2xl text-slate-700 focus:border-red-100 focus:bg-white"
                        value={deliveryMode}
                        onChange={(e) => setDeliveryMode(e.target.value)}
                    >
                        <option value="click_collect">Retrait en magasin</option>
                        <option value="delivery">Livraison à domicile</option>
                    </select>
                    <ChevronDown size={20} className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-4 top-1/2" />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 mb-4 text-xs font-black tracking-widest text-gray-400 uppercase">
                    <Phone size={14} className="text-[#f34141]" />
                    Téléphone
                  </label>
                  <input
                    className="w-full p-4 font-bold transition-all border-2 border-transparent outline-none bg-gray-50 rounded-2xl text-slate-700 focus:border-red-100 focus:bg-white"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    type="tel"
                    placeholder="Ex: 41880217"
                  />
                </div>
              </div>

              <div className="mt-8">
                <label className="flex items-center gap-2 mb-4 text-xs font-black tracking-widest text-gray-400 uppercase">
                  <MapPin size={14} className="text-[#f34141]" />
                  {deliveryMode === "delivery" ? "Adresse de livraison" : "Adresse de retrait"}
                </label>
                <div className="relative">
                    {addresses.length > 0 ? (
                        <div className="relative">
                            <select
                                className="w-full p-4 pr-10 font-bold transition-all border-2 border-transparent outline-none appearance-none cursor-pointer bg-gray-50 rounded-2xl text-slate-700 focus:border-red-100 focus:bg-white"
                                value={selectedAddress?._id || ""}
                                onChange={(e) => setSelectedAddress(addresses.find((a) => a._id === e.target.value))}
                            >
                                {addresses.map((addr) => (
                                <option key={addr._id} value={addr._id}>
                                    {addr.label ? `${addr.label} – ` : ""}{addr.street}
                                </option>
                                ))}
                            </select>
                            <ChevronDown size={20} className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-4 top-1/2" />
                        </div>
                    ) : (
                    <input
                        className="w-full p-4 font-bold transition-all border-2 border-transparent outline-none bg-gray-50 rounded-2xl text-slate-700 focus:border-red-100 focus:bg-white"
                        placeholder={deliveryMode === "delivery" ? "Votre quartier ou adresse précise" : "Précisez votre zone de retrait"}
                        value={manualAddress}
                        onChange={(e) => setManualAddress(e.target.value)}
                    />
                    )}
                </div>
              </div>
            </div>
            
            {/* PAIEMENT & CAPTURE */}
          <div className="p-8 bg-white border border-gray-100 shadow-sm rounded-[2.5rem]">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <label className="flex items-center gap-2 mb-4 text-xs font-black tracking-widest text-gray-400 uppercase">
                  <Wallet size={14} className="text-[#f34141]" />
                  Mode de paiement
                </label>
                <div className="relative">
                  <select
                    className="w-full p-4 pr-10 font-bold transition-all border-2 border-transparent outline-none appearance-none cursor-pointer bg-gray-50 rounded-2xl text-slate-700 focus:border-red-100 focus:bg-white"
                    value={payment}
                    onChange={(e) => setPayment(e.target.value)}
                  >
                    {deliveryMode !== "delivery" && (
                        <option value="cash">💵 Cash au comptoir</option>
                    )}
                    <option value="mobile">📱 Bankily / Masrivi / Autre</option>
                  </select>
                  <ChevronDown size={20} className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-4 top-1/2" />
                </div>
              </div>

              <div className={`transition-all duration-300 ${payment === 'mobile' ? 'opacity-100' : 'opacity-40 cursor-not-allowed'}`}>
                <label className="flex items-center gap-2 mb-4 text-xs font-black tracking-widest text-gray-400 uppercase">
                  <Camera size={14} className="text-[#f34141]" />
                  {deliveryMode === "delivery" ? "Preuve (Obligatoire)" : "Preuve (Optionnel)"}
                </label>
                  <div className="relative group">
                    <input
                        disabled={payment !== 'mobile'}
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPaymentProof(e.target.files[0])}
                        className="absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <div className="flex items-center justify-center gap-2 p-4 font-bold bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 group-hover:border-[#f34141]/30 transition-colors">
                        <Camera size={20} />
                        <span className="text-sm truncate">
                            {paymentProof ? paymentProof.name : "Joindre capture"}
                        </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION DYNAMIQUE DES NUMÉROS DE PAIEMENT */}
              {payment === 'mobile' && (
                <div className="p-6 mt-8 border border-red-100 bg-red-50/30 rounded-3xl animate-fade-in">
                  <label className="flex items-center gap-2 mb-4 text-[10px] font-black tracking-widest text-[#f34141] uppercase">
                    <Zap size={12} fill="currentColor" />
                    Numéros de paiement du marchand
                  </label>
                  
                  <div className="grid gap-3">
                    {merchantInfos?.paymentMethods && merchantInfos.paymentMethods.length > 0 ? (
                      merchantInfos.paymentMethods.map((p, i) => {
                        const nom = typeof p === 'string' ? p : (p.label || p.method || "Paiement");
                        const num = typeof p === 'string' ? "" : (p.value || p.phone || p.number || "");
                        return <PaymentBadge key={i} nomPaiement={nom} numero={num} />;
                      })
                    ) : (
                      <p className="text-xs font-bold text-gray-500 italic py-2">
                        Contactez le marchand au {merchantInfos?.phone || "..."} pour les détails du transfert.
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-3 p-4 mt-6 text-blue-700 border border-blue-100 bg-blue-50/50 rounded-2xl">
                    <Clock size={16} className="flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] font-bold leading-relaxed">
                      Astuce : Copiez le numéro ci-dessus, effectuez votre transfert sur votre application bancaire, puis revenez joindre la capture d'écran pour valider.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ACTION BUTTON & RASSURANCE */}
            <div className="pt-4 space-y-6 text-center">
                <button
                onClick={confirm}
                disabled={uploadingProof}
                className="group relative flex items-center justify-center w-full py-6 font-black text-xl text-white bg-gradient-to-r from-[#f34141] to-[#ff6b6b] rounded-[2rem] shadow-xl shadow-red-500/25 hover:shadow-red-500/40 transition-all duration-300 active:scale-[0.98] disabled:opacity-50"
                >
                {uploadingProof ? (
                    <div className="w-6 h-6 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
                ) : (
                    <>
                        <span>Confirmer ma commande</span>
                        <CheckCircle2 size={24} className="ml-3 transition-transform duration-300 group-hover:scale-110" />
                    </>
                )}
                </button>
                
                <p className="text-gray-400 text-[11px] font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                    <Info size={14} className="text-gray-300" />
                    Aucun paiement n'est effectué directement sur Souqi
                </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
